import { useEffect, useState } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { api, getToken, onAuthChange, setToken } from './api';
import { initNative, registerPush } from './native';
import { LoginPage } from './pages/LoginPage';
import { ProfilePage } from './pages/ProfilePage';
import { SearchesPage } from './pages/SearchesPage';
import { SearchFormPage } from './pages/SearchFormPage';
import { ResultsPage } from './pages/ResultsPage';
import { UserPage } from './pages/UserPage';
import { MessagesPage } from './pages/MessagesPage';
import { ChatPage } from './pages/ChatPage';
import { FeedPage } from './pages/FeedPage';
import { SettingsPage } from './pages/SettingsPage';
import { Icon } from './components/Icon';
import { PostsPage } from './pages/PostsPage';

const TITLES: Record<string, string> = { '/profile': 'My profile', '/searches': 'Searches', '/posts': 'Feed', '/messages': 'Messages', '/feed': 'Activity', '/settings': 'Settings' };
const BADGE_POLL_MS = 30_000;

export function App() {
  const [authed, setAuthed] = useState(!!getToken());
  const [badges, setBadges] = useState({ chat: 0, feed: 0 });
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => onAuthChange(() => setAuthed(!!getToken())), []);

  // Native: splash/status bar, and OAuth results arriving through bandwagn://auth/...#token=…
  useEffect(() => {
    initNative((frag) => {
      if (frag.get('token')) { setToken(frag.get('token')); nav(frag.get('created') === '1' ? '/profile' : '/searches', { replace: true }); }
      else if (frag.get('linked')) nav(`/settings#linked=${frag.get('linked')}`, { replace: true });
      else if (frag.get('error')) nav(`/login#error=${encodeURIComponent(frag.get('error')!)}`, { replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Register for push once signed in (no-op on the web; server is dark until credentials exist).
  useEffect(() => { if (authed) registerPush().catch(() => {}); }, [authed]);

  // Tab badges: poll, and re-check on every navigation (visiting a tab clears it).
  useEffect(() => {
    if (!authed) return;
    const tick = () => Promise.all([api.chatUnread(), api.feedUnread()])
      .then(([c, f]) => setBadges({ chat: c.unread, feed: f.unread }))
      .catch(() => {});
    tick();
    const t = window.setInterval(tick, BADGE_POLL_MS);
    return () => window.clearInterval(t);
  }, [authed, loc.pathname]);

  if (!authed) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const isRoot = loc.pathname in TITLES && loc.pathname !== '/settings';
  return (
    <div className="app">
      <header className="topbar">
        {!isRoot && <button className="back" onClick={() => nav(-1)}><Icon name="back" size={18} /> Back</button>}
        <h1>{TITLES[loc.pathname] ?? 'Bandwagn'}</h1>
        {loc.pathname === '/profile' && <Link to="/settings" className="btn ghost sm" aria-label="Settings"><Icon name="settings" size={24} /></Link>}
      </header>
      <main className="page">
        <Routes>
          <Route path="/" element={<Navigate to="/searches" replace />} />
          <Route path="/login" element={<Navigate to="/searches" replace />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/searches" element={<SearchesPage />} />
          <Route path="/searches/new" element={<SearchFormPage />} />
          <Route path="/searches/:id/edit" element={<SearchFormPage />} />
          <Route path="/searches/:id/results" element={<ResultsPage />} />
          <Route path="/results" element={<ResultsPage />} />
          <Route path="/users/:id" element={<UserPage />} />
          <Route path="/posts" element={<PostsPage />} />
          <Route path="/feed" element={<FeedPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/messages/:roomId" element={<ChatPage />} />
        </Routes>
      </main>
      <nav className="tabbar">
        <NavLink to="/searches"><span><Icon name="search" size={26} /></span>Search</NavLink>
        <NavLink to="/posts"><span><Icon name="feed" size={26} /></span>Feed</NavLink>
        <NavLink to="/feed"><span><Icon name="activity" size={26} />{badges.feed > 0 && <i className="dot" />}</span>Activity</NavLink>
        <NavLink to="/messages"><span><Icon name="messages" size={26} />{badges.chat > 0 && <i className="dot" />}</span>Messages</NavLink>
        <NavLink to="/profile"><span><Icon name="profile" size={26} /></span>Profile</NavLink>
      </nav>
    </div>
  );
}
